// Features: M004-F0795, M004-F0796 (WebUI #263, slice A: pure helpers, no React)
// Tests: T-M004-0414 (group validation), T-M004-0416 (apportionment validation and window wording), T-M004-0417 (refusal mapper),
//        T-M004-0422 (reason rule), T-M004-0426 (effective-from mapping), T-M004-0428 (save summary sentences)
//
// The UX mirror of the Ledger's rules (contract 1.27.0). The Ledger is the judge: nothing here decides a save on its own, and nothing
// here matches a booking to a group (the preview's routing is the reply's, T-M004-0421).
import { errorCode, errorDetails } from '../../../services/billingProfileAdapter';

export const REASON_MIN_LENGTH = 3;
export const APPORTIONMENT_HEADING = 'Bill part of matching bookings to another group';
const MAX_GROUPS = 12;
const MAX_RULES = 8;
const MAX_PARTS = 6;
const MIN_PARTS = 2;
const GROUP_KEY = /^[a-z0-9_-]{1,40}$/;
const RULE_ID = /^[a-z0-9_-]{1,40}$/;
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NATURES = ['class', 'rehearsal', 'other'];
// Inline bill-to ("Someone else") limits, as the Ledger states them (Ledger decision 2026-10-20).
export const ADDRESS_MAX_LINES = 5;
export const ADDRESS_LINE_MAX = 120;
const INLINE_NAME_MAX = 120;
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isPresent = (v) => v !== undefined && v !== null && v !== '';

// ---- dates and times --------------------------------------------------------------------------------------------------
// A real calendar date in YYYY-MM-DD form (rejects 2026-02-30).
export function isCalendarDate(value) {
  if (typeof value !== 'string' || !YMD.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

// Offset of Europe/London from UTC, in minutes, at the instant `utcMs` (BST +60, GMT 0).
function londonOffsetMinutes(utcMs) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - utcMs) / 60000);
}

// The London calendar date (YYYY-MM-DD) of an instant.
export function londonDateOf(instant = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
}

// 00:00 Europe/London on a calendar date, as an ISO instant (BST and GMT aware), e.g. 2026-11-03 -> 2026-11-03T00:00:00Z.
export function londonMidnightIso(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - londonOffsetMinutes(guess) * 60000;
  const offset = londonOffsetMinutes(first);
  const instant = guess - offset * 60000;
  return new Date(instant).toISOString().replace('.000Z', 'Z');
}

// One sentence for today, a past date, and the server's LEDGER_PROFILE_EFFECTIVE_FROM_PAST alike (decision 2, 2026-10-20).
export const EFFECTIVE_FROM_PAST = 'Choose Now, or a date from tomorrow.';

// The request's effective_from. "now" is omitted (undefined: the Ledger applies it at once). A future date is sent as the ISO instant of
// 00:00 Europe/London. A past date, or today (its midnight has already passed), is refused locally with its sentence and no value.
export function effectiveFromFor(choice, date, now = new Date()) {
  if (choice === 'now') return undefined;
  if (choice !== 'future') throw new Error('Choose "Now" or a future date.');
  if (!isCalendarDate(date)) throw new Error('Enter a date as YYYY-MM-DD.');
  const today = londonDateOf(now);
  if (date <= today) throw new Error(EFFECTIVE_FROM_PAST);
  return londonMidnightIso(date);
}

// The half-open booking window, in words: "from 18:00, before 22:00" (start >= from and start < to).
export function timeWindowText(from, to) {
  const parts = [];
  if (isPresent(from)) parts.push(`from ${from}`);
  if (isPresent(to)) parts.push(`before ${to}`);
  return parts.length ? parts.join(', ') : 'at any time';
}

// ---- validation -------------------------------------------------------------------------------------------------------
const push = (out, path, message) => out.push({ path, message });

// Shared match vocabulary for groups and apportionment rules. ALL given keys must hold; at least one key is required.
function validateMatch(match, base, out) {
  if (!isObject(match)) { push(out, base, 'Give this group at least one condition.'); return; }
  const keys = Object.keys(match).filter((k) => isPresent(match[k]));
  if (keys.length === 0) push(out, base, 'Give this group at least one condition.');
  if (isPresent(match.description_contains)) {
    const s = String(match.description_contains);
    if (s.length < 1 || s.length > 80) push(out, `${base}.description_contains`, 'Use 1 to 80 characters.');
  }
  if (isPresent(match.weekday)) {
    const days = Array.isArray(match.weekday) ? match.weekday : [match.weekday];
    const unique = new Set(days);
    if (days.length < 1 || days.length > 7 || unique.size !== days.length || days.some((d) => !WEEKDAYS.includes(d))) {
      push(out, `${base}.weekday`, 'Choose one or more weekdays, each once.');
    }
  }
  if (isPresent(match.start_time_from) && !HHMM.test(match.start_time_from)) push(out, `${base}.start_time_from`, 'Use a time as HH:MM.');
  if (isPresent(match.start_time_to) && !HHMM.test(match.start_time_to)) push(out, `${base}.start_time_to`, 'Use a time as HH:MM.');
  if (isPresent(match.room_id) && (String(match.room_id).length < 1 || String(match.room_id).length > 64)) push(out, `${base}.room_id`, 'Use 1 to 64 characters.');
  if (isPresent(match.nature_of_use) && !NATURES.includes(match.nature_of_use)) push(out, `${base}.nature_of_use`, 'Choose class, rehearsal or other.');
  if (isPresent(match.booking_id) && (String(match.booking_id).length < 1 || String(match.booking_id).length > 64)) push(out, `${base}.booking_id`, 'Use 1 to 64 characters.');
  if (isPresent(match.date_from) && !isCalendarDate(match.date_from)) push(out, `${base}.date_from`, 'Use a date as YYYY-MM-DD.');
  if (isPresent(match.date_to) && !isCalendarDate(match.date_to)) push(out, `${base}.date_to`, 'Use a date as YYYY-MM-DD.');
}

function validateGroups(groups, out) {
  if (groups.length > MAX_GROUPS) push(out, 'groups', `Use at most ${MAX_GROUPS} groups.`);
  const seen = new Set();
  groups.forEach((g, i) => {
    const base = `groups[${i}]`;
    if (!isObject(g)) { push(out, base, 'This group is not valid.'); return; }
    const key = g.group_key;
    if (key === 'default') push(out, `${base}.group_key`, 'default is reserved. Choose another key.');
    else if (!GROUP_KEY.test(key ?? '')) push(out, `${base}.group_key`, 'Use 1 to 40 lower-case letters, digits, _ or -.');
    else if (seen.has(key)) push(out, `${base}.group_key`, 'Each group needs its own key.');
    if (typeof key === 'string') seen.add(key);
    const label = g.label ?? '';
    if (label.length < 1 || label.length > 80) push(out, `${base}.label`, 'Use 1 to 80 characters.');
    validateMatch(g.match, `${base}.match`, out);
    const billTo = g.bill_to;
    if (isObject(billTo)) {
      const kind = billTo.kind;
      if (kind !== 'customer' && kind !== 'contact' && kind !== 'inline') push(out, `${base}.bill_to.kind`, 'Choose who to bill.');
      if (kind === 'contact' && !(Number.isSafeInteger(billTo.contact_user_id) && billTo.contact_user_id >= 1)) {
        push(out, `${base}.bill_to.contact_user_id`, 'Choose a contact.');
      }
      if (kind === 'inline') {
        const name = billTo.display_name ?? '';
        if (typeof name !== 'string' || name.trim().length < 1 || name.length > INLINE_NAME_MAX) {
          push(out, `${base}.bill_to.display_name`, `Use 1 to ${INLINE_NAME_MAX} characters.`);
        }
        const lines = billTo.address_lines;
        if (lines !== undefined) {
          if (!Array.isArray(lines) || lines.length > ADDRESS_MAX_LINES) push(out, `${base}.bill_to.address_lines`, `Use at most ${ADDRESS_MAX_LINES} address lines.`);
          else lines.forEach((line, j) => {
            if (typeof line !== 'string' || line.length > ADDRESS_LINE_MAX) push(out, `${base}.bill_to.address_lines[${j}]`, `Use at most ${ADDRESS_LINE_MAX} characters.`);
          });
        }
      }
      // Inline needs an email to send to; the customer and contact kinds take an optional one. A given email must be well-formed.
      const emailMissing = kind === 'inline' && !isPresent(billTo.email);
      if (emailMissing || (isPresent(billTo.email) && (!EMAIL.test(billTo.email) || billTo.email.length > 254))) {
        push(out, `${base}.bill_to.email`, 'Enter an email address such as name@example.com.');
      }
    }
  });
}

// waterfall: ordered parts; every part but one has a whole-pence fixed amount, exactly one part takes the remainder.
// ratio: every part has a whole-number weight >= 1; no fixed amount and no remainder.
function validatePart(part, base, mode, out) {
  if (!isObject(part)) { push(out, base, 'This part is not valid.'); return; }
  if (mode === 'waterfall') {
    if (part.remainder === true) {
      if (isPresent(part.fixed_minor)) push(out, `${base}.fixed_minor`, 'The part that takes the rest has no fixed amount.');
    } else if (!(Number.isSafeInteger(part.fixed_minor) && part.fixed_minor >= 1)) {
      push(out, `${base}.fixed_minor`, 'A fixed amount must be at least 0.01.');
    }
    if (isPresent(part.weight)) push(out, `${base}.weight`, 'A waterfall part has no share.');
  } else {
    if (!(Number.isSafeInteger(part.weight) && part.weight >= 1)) push(out, `${base}.weight`, 'Use a whole number of at least 1.');
    if (isPresent(part.fixed_minor) || part.remainder === true) push(out, base, 'A share cannot have a fixed amount or the remainder.');
  }
}

function validateApportionments(rules, groupKeys, out) {
  if (rules.length > MAX_RULES) push(out, 'apportionments', `Use at most ${MAX_RULES} rules.`);
  rules.forEach((r, i) => {
    const base = `apportionments[${i}]`;
    if (!isObject(r)) { push(out, base, 'This rule is not valid.'); return; }
    if (!RULE_ID.test(r.rule_id ?? '')) push(out, `${base}.rule_id`, 'Use 1 to 40 lower-case letters, digits, _ or -.');
    const label = r.label ?? '';
    if (label.length < 1 || label.length > 80) push(out, `${base}.label`, 'Use 1 to 80 characters.');
    validateMatch(r.match, `${base}.match`, out);
    if (r.mode !== 'waterfall' && r.mode !== 'ratio') { push(out, `${base}.mode`, 'Choose waterfall or ratio.'); return; }
    const parts = Array.isArray(r.parts) ? r.parts : [];
    if (parts.length < MIN_PARTS || parts.length > MAX_PARTS) push(out, `${base}.parts`, `Use ${MIN_PARTS} to ${MAX_PARTS} parts.`);
    parts.forEach((p, j) => {
      const pBase = `${base}.parts[${j}]`;
      if (!isObject(p)) { push(out, pBase, 'This part is not valid.'); return; }
      if (!groupKeys.has(p.group_key)) push(out, `${pBase}.group_key`, 'Choose a group that exists in this profile.');
      validatePart(p, pBase, r.mode, out);
    });
    if (r.mode === 'waterfall') {
      const remainders = parts.filter((p) => isObject(p) && p.remainder === true).length;
      if (remainders !== 1) push(out, `${base}.parts`, 'Set exactly one part to take the remainder.');
    }
  });
}

// Returns [{ path, message }] using the contract's details.path format (for example groups[1].bill_to.email). Empty = passes the mirror.
export function validateProfileDraft(profile) {
  const out = [];
  const groups = Array.isArray(profile?.groups) ? profile.groups : [];
  const rules = Array.isArray(profile?.apportionments) ? profile.apportionments : [];
  validateGroups(groups, out);
  const groupKeys = new Set(groups.filter(isObject).map((g) => g.group_key));
  validateApportionments(rules, groupKeys, out);
  return out;
}

// The reason rule: at least 3 characters after trimming. Returns the sentence, or null when it passes.
export function validateReason(reason) {
  return String(reason ?? '').trim().length >= REASON_MIN_LENGTH ? null : `Give a reason of at least ${REASON_MIN_LENGTH} characters.`;
}

// ---- apportionment wording --------------------------------------------------------------------------------------------
const money = (minor) => `£${(Number(minor) / 100).toFixed(2)}`;
const labelOf = (groups, key) => (groups || []).find((g) => g?.group_key === key)?.label || key;

// "£75.00 to Jammers, the rest to Flying Feet" (waterfall) or "3 shares to Jammers, 1 share to Flying Feet" (ratio). Never "split".
export function apportionmentText(rule, groups) {
  const parts = Array.isArray(rule?.parts) ? rule.parts : [];
  return parts.map((p) => {
    const to = labelOf(groups, p.group_key);
    if (rule.mode === 'ratio') return `${p.weight} ${p.weight === 1 ? 'share' : 'shares'} to ${to}`;
    if (p.remainder === true) return `the rest to ${to}`;
    return `${money(p.fixed_minor)} to ${to}`;
  }).join(', ');
}

// ---- refusals ---------------------------------------------------------------------------------------------------------
export const UNKNOWN_REFUSAL = 'Something went wrong while saving. Reload to check what is saved.';

// The generic read-failure sentence (design §7): a failed read never says "saving".
export const READ_FAILED = "Couldn't load this. Try again.";

// The Ledger's LEDGER_PROFILE_INVALID message is never shown (decision 3, 2026-10-20): the sentence is generic and sits on details.path's field.
export const FIELD_INVALID = "This value isn't accepted. Check it and try again.";

const STATIC_SENTENCES = Object.freeze({
  LEDGER_OWN_ACCOUNT_REFUSED: 'Money actions on your own account are not permitted.',
  LEDGER_ACCOUNT_PERMISSION_DENIED: 'You need Ledger manager access in this organisation to change billing profiles.',
  LEDGER_ACCOUNT_SAL_REQUIRED: 'Saving needs a fresh step-up sign-in. Sign in again, then try once more.',
  STEP_UP_REQUIRED: 'Saving needs a step-up sign-in to continue.',
  LEDGER_REASON_REQUIRED: 'Give a reason of at least 3 characters.',
  LEDGER_PROFILE_EFFECTIVE_FROM_PAST: EFFECTIVE_FROM_PAST,
  LEDGER_PROFILE_UNKNOWN_SETTING: 'This page sent a setting the Ledger does not know. Reload and try again.',
  LEDGER_BILLTO_CONTACT_NOT_FOUND: 'That contact does not exist. Add it, or choose another.',
  LEDGER_CONTACT_NOT_FOUND: 'This customer is no longer in the ledger. Close the drawer and reload.',
  LEDGER_HA_IDEMPOTENCY_CONFLICT: 'A different save was already recorded with this request. Reload to see what is saved.',
  LEDGER_DRAFT_SELECTOR_REQUIRED: 'More than one draft is open for this customer. Choose which draft to use, then try again.',
});

// The field a LEDGER_PROFILE_INVALID refusal belongs to (its details.path, e.g. groups[1].bill_to.email), or null for any other refusal.
export function refusalPath(outcome) {
  const code = typeof outcome?.code === 'string' ? outcome.code : errorCode(outcome);
  if (code !== 'LEDGER_PROFILE_INVALID') return null;
  const details = isObject(outcome?.details) ? outcome.details : errorDetails(outcome);
  return typeof details.path === 'string' ? details.path : null;
}

// Maps a refusal to the one sentence the page shows. `outcome` is { code, details, message } or an axios-style error. A known code with
// details.path names the field. An unknown code gets UNKNOWN_REFUSAL and never a raw server message.
export function refusalSentence(outcome) {
  const code = typeof outcome?.code === 'string' ? outcome.code : errorCode(outcome);
  const details = isObject(outcome?.details) ? outcome.details : errorDetails(outcome);
  if (code === 'LEDGER_PROFILE_INVALID') return FIELD_INVALID;
  if (code === 'LEDGER_PROFILE_REVISION_CONFLICT') {
    const rev = Number.isInteger(details.current_rev) ? ` (now revision ${details.current_rev})` : '';
    return `Someone else changed this billing profile${rev}. Your edits are kept below; the saved values are shown beside them.`;
  }
  if (code === 'LEDGER_PROFILE_AMBIGUOUS_MATCH') {
    const rules = Array.isArray(details.rules) ? details.rules.map(String) : [];
    const names = rules.length ? rules.join(', ') : 'two rules';
    return `These rules match the same bookings with equal priority: ${names}. Make one of them narrower.`;
  }
  if (Object.prototype.hasOwnProperty.call(STATIC_SENTENCES, code) && STATIC_SENTENCES[code]) return STATIC_SENTENCES[code];
  return UNKNOWN_REFUSAL;
}

// ---- save summary -----------------------------------------------------------------------------------------------------
// The sentences shown after a successful save, from the reply only. Issued invoices never change, always.
export function summariseSave(reply) {
  const drafts = Array.isArray(reply?.affected_drafts) ? reply.affected_drafts : [];
  const count = (outcome) => drafts.filter((d) => d?.outcome === outcome).length;
  const plural = (n, one, many) => (n === 1 ? one : many);
  const sentences = [];
  const recomputed = count('recomputed');
  const kept = count('kept_hand_placed');
  const unchanged = count('unchanged');
  if (recomputed) sentences.push(`${recomputed} ${plural(recomputed, 'draft was', 'drafts were')} recomputed.`);
  if (kept) sentences.push(`Your hand-placed lines were kept on ${kept} ${plural(kept, 'draft', 'drafts')}.`);
  if (unchanged) sentences.push(`${unchanged} ${plural(unchanged, 'draft has', 'drafts have')} no change.`);
  sentences.push('Issued invoices never change.');
  const untouched = Number.isInteger(reply?.issued_untouched) ? reply.issued_untouched : 0;
  sentences.push(untouched === 0 ? 'No issued invoices were affected.' : `${untouched} issued ${plural(untouched, 'invoice was', 'invoices were')} not touched.`);
  return sentences;
}
